import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AccountService } from 'app/core';
import { AccountHook } from './account-hooks.types';

@Injectable({
  providedIn: 'root',
})
export class AccountHooksService {
  // Private
  private _accountHook: BehaviorSubject<AccountHook | null>;
  private _accountHooks: BehaviorSubject<AccountHook[] | null>;
  private _accountId: string;
  selectedAccountHookChanged: BehaviorSubject<any>;

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(
    private _httpClient: HttpClient,
    private _accountService: AccountService,
  ) {
    // Set the private defaults
    this._accountHook = new BehaviorSubject(null);
    this._accountHooks = new BehaviorSubject(null);
    this.selectedAccountHookChanged = new BehaviorSubject(null);

    this._accountService.currentAccount.subscribe((accountData) => {
      this._accountId = accountData.id;
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for contact
   */
  get accountHook$(): Observable<AccountHook> {
    return this._accountHook.asObservable();
  }

  /**
   * Getter for contacts
   */
  get accountHooks$(): Observable<AccountHook[]> {
    return this._accountHooks.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  /**
   * Get contact by id
   */
  getAccountHookById(id: string): Observable<AccountHook> {
    return this._httpClient.get<any>(`account_hooks/${id}`).pipe(
      map((response) => {
        this._accountHook.next(response.account_hook);
        return response.account_hook;
      }),
    );
  }

  saveAccountHook(accountHook: AccountHook): Observable<AccountHook> {
    if (accountHook.id) {
      return this.accountHooks$.pipe(
        take(1),
        switchMap((accountHooks) =>
          this._httpClient
            .put<any>(`account_hooks/${accountHook.id}`, {
              account_hook: accountHook,
            })
            .pipe(
              map((response) => {
                let updateAccountHook = response.account_hook;
                // Find the index of the updated contact
                if (accountHooks) {
                  const index = accountHooks.findIndex((item) => item.id === accountHook.id);

                  // Update the contact
                  accountHooks[index] = updateAccountHook;

                  // Update the contacts
                  this._accountHooks.next(accountHooks);
                }

                // Return the updated contact
                return updateAccountHook;
              }),
              switchMap((updateAccountHook) =>
                this.accountHook$.pipe(
                  take(1),
                  filter((item) => item && +item.id === +updateAccountHook.id),
                  tap(() => {
                    // Update the contact if it's selected
                    this._accountHook.next(updateAccountHook);

                    // Return the updated contact
                    return updateAccountHook;
                  }),
                ),
              ),
            ),
        ),
      );
    } else {
      return this.accountHooks$.pipe(
        take(1),
        switchMap((accountHooks) =>
          this._httpClient.post<any>(`account_hooks`, { account_hook: accountHook }).pipe(
            map((reponse) => {
              if (accountHooks) {
                // Update the contacts with the new contact
                this._accountHooks.next([reponse.account_hook, ...accountHooks]);
              }

              //update so that we can add properties
              this._accountHook.next(reponse.account_hook);

              // Return the new contact
              return reponse.account_hook;
            }),
          ),
        ),
      );
    }
  }

  deleteAccountHook(id: number): Observable<any> {
    return this.accountHooks$.pipe(
      take(1),
      switchMap((accountHooks) =>
        this._httpClient.delete<any>(`account_hooks/${id}`).pipe(
          map((response) => {
            // Find the index of the deleted label within the labels
            const index = accountHooks.findIndex((item) => item.id == id);

            // Delete the label
            accountHooks.splice(index, 1);

            // Update the labels
            this._accountHooks.next(accountHooks);

            // Return the deleted status
            return response.account_hook;
          }),
        ),
      ),
    );
  }

  getAccountHooks(): Observable<AccountHook[]> {
    return this._httpClient.get<any>(`account_hooks`).pipe(
      map((response) => {
        this._accountHooks.next(response.account_hooks);

        return response.account_hooks;
      }),
    );
  }
}
